import { Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@nucleus-commerce/ui/components/button';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import { ProductPicker, type ProductPickerRow } from '@/features/resource-pickers';

import { emptyCta } from '../api/mappers';
import { ctaLinkTypes, MAX_CTAS } from '../schemas/block-form-schema';

import { CollectionField } from './collection-field';
import { fieldText, type LooseForm } from './field-text';

export interface CtaFieldsProps {
  /** Path of the `ctas` array, e.g. `items.0.ctas` or `header.ctas`. */
  name: string;
  /** Null for shared blocks: collections are store-scoped, so that link type is not offered. */
  storeId: string | null;
}

/** Opens the shared product picker for one master product. */
function CtaProductButton({
  productId,
  storeId,
  onPick,
}: {
  productId: string;
  storeId: string | null;
  onPick: (product: ProductPickerRow<{ kind: 'org' }>) => void;
}) {
  const { t } = useTranslation('content');
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
        {productId ? t('cta.changeProduct') : t('cta.pickProduct')}
      </Button>
      <ProductPicker
        open={open}
        onOpenChange={setOpen}
        scope={{ kind: 'org' }}
        {...(storeId ? { storeId } : {})}
        multiple={false}
        initialSelectedIds={productId ? [productId] : []}
        onConfirm={(selection) => {
          const [picked] = selection.items;
          if (picked) onPick(picked);
        }}
      />
    </>
  );
}

/** Up to three calls to action per item: a URL, a product or a collection of the store. */
export function CtaFields({ name, storeId }: CtaFieldsProps) {
  const { t } = useTranslation('content');
  const form = useFormContext<LooseForm>();
  const { fields, append, remove } = useFieldArray({ control: form.control, name });
  const path = (index: number, field: string) => `${name}.${index}.${field}`;
  const linkTypes = ctaLinkTypes.filter((type) => storeId !== null || type !== 'collection');

  return (
    <div className="flex flex-col gap-3" role="group" aria-label={t('cta.group')}>
      {fields.map((row, index) => {
        const linkType = fieldText(form.watch(path(index, 'linkType')));
        const productTitle = fieldText(form.watch(path(index, 'productTitle')));
        const productId = fieldText(form.watch(path(index, 'productId')));
        return (
          <div
            key={row.id}
            className="grid gap-3 rounded-md border p-3 md:grid-cols-2"
            role="group"
            aria-label={t('cta.nth', { index: index + 1 })}
          >
            <div className="flex items-center justify-between md:col-span-2">
              <span className="text-sm font-medium">{t('cta.nth', { index: index + 1 })}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => remove(index)}
                aria-label={t('cta.remove', { index: index + 1 })}
              >
                <X aria-hidden />
              </Button>
            </div>
            <FormField
              control={form.control}
              name={path(index, 'label')}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('cta.label')}</FormLabel>
                  <FormControl>
                    <Input {...field} value={fieldText(field.value)} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={path(index, 'linkType')}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('cta.linkType')}</FormLabel>
                  <Select value={fieldText(field.value)} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {linkTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {t(`cta.linkTypes.${type}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            {linkType === 'url' ? (
              <FormField
                control={form.control}
                name={path(index, 'url')}
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>{t('cta.url')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={fieldText(field.value)}
                        placeholder="/collections/sale"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : linkType === 'product' ? (
              <FormField
                control={form.control}
                name={path(index, 'productId')}
                render={() => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>{t('cta.product')}</FormLabel>
                    <div className="flex items-center gap-2">
                      <span className="min-w-0 flex-1 truncate text-sm">
                        {productId ? productTitle || productId : t('cta.noProduct')}
                      </span>
                      <CtaProductButton
                        productId={productId}
                        storeId={storeId}
                        onPick={(picked) => {
                          form.setValue(path(index, 'productId'), picked.id, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                          form.setValue(path(index, 'productTitle'), picked.title, {
                            shouldDirty: true,
                          });
                        }}
                      />
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : storeId !== null ? (
              <FormField
                control={form.control}
                name={path(index, 'collectionId')}
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>{t('cta.collection')}</FormLabel>
                    <CollectionField
                      storeId={storeId}
                      value={fieldText(field.value)}
                      onChange={field.onChange}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}
            <FormField
              control={form.control}
              name={path(index, 'style')}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('cta.style')}</FormLabel>
                  <FormControl>
                    <Input {...field} value={fieldText(field.value)} placeholder="primary" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={path(index, 'openInNewTab')}
              render={({ field }) => (
                <FormItem className="flex items-center gap-2 pt-6">
                  <FormControl>
                    <Switch
                      checked={field.value === true}
                      onCheckedChange={field.onChange}
                      id={`${row.id}-newtab`}
                    />
                  </FormControl>
                  <FormLabel htmlFor={`${row.id}-newtab`}>{t('cta.openInNewTab')}</FormLabel>
                </FormItem>
              )}
            />
          </div>
        );
      })}
      {fields.length < MAX_CTAS ? (
        <div>
          <Button type="button" variant="outline" size="sm" onClick={() => append(emptyCta())}>
            <Plus aria-hidden />
            {t('cta.add')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
